import { createInstance, getDefinition } from "./registry.js";
import { durationText } from "./units.js";
import {
  E,
  clamp,
  numeric,
  short,
  text,
  line,
  rect,
  circle,
  path,
  point,
  arc,
  sample,
  val,
  reading,
  missing,
  simple,
  svgId,
  part,
} from "./render-kit.js";

export const systemRendererIds = Object.freeze([
  "action",
  "active-button",
  "rocker",
  "vertical-switch",
  "momentary",
  "dimmer",
  "lighting-color",
  "climate",
  "ice-maker",
  "watermaker",
  "stereo",
  "mode",
  "alerts",
  "watchkeeping",
  "notification",
  "camera",
  "camera-ptz",
  "web-page",
  "diagram",
  "note",
  "divider",
]);

const motion = 'data-motion="shape"';
const bold = 'font-weight="650"';
const muted = "var(--muted)";
const accent = "var(--accent)";
const pad = (n) => String(n).padStart(2, "0");

function pill(x, y, w, h, label, active = false, small = false) {
  return (
    rect(
      x,
      y,
      w,
      h,
      active ? accent : "var(--surface)",
      h / 2,
      `stroke="${active ? accent : "var(--grid)"}" ${motion}`,
    ) +
    text(
      x + w / 2,
      y + h / 2 + (small ? 4 : 5),
      label,
      small ? 11 : 14,
      active ? "var(--background)" : "var(--text)",
      "middle",
      bold,
    )
  );
}

function labelState(data, o) {
  const svc = data.service ?? {};
  const raw = svc.state ?? val(data, "state");
  const source = sample(data, "state");
  if (
    svc.available === false ||
    ["missing", "disconnected"].includes(source?.quality)
  )
    return { raw: null, label: "Unavailable", active: false, known: false };
  return {
    raw,
    label:
      o.stateLabels?.[String(raw)] ??
      svc.stateLabel ??
      (raw == null ? "Unknown" : String(raw)),
    active: raw === true || raw === "On" || raw === "on",
    known: raw != null,
  };
}

function fault(data, y = 244) {
  return data.service?.fault
    ? text(200, y, short(data.service.fault, 48), 11, "var(--danger)", "middle")
    : "";
}

function serviceReading(data, role, o) {
  const source = sample(data, role);
  const available =
    data.service?.available !== false &&
    source &&
    !["missing", "disconnected"].includes(source.quality);
  return {
    ...reading(data, role, o),
    numeric: available && numeric(source.value) ? source.value : null,
    available: Boolean(available),
  };
}

function metric(
  data,
  role,
  o,
  x,
  y,
  size,
  anchor = "start",
  color = "var(--text)",
) {
  const r = serviceReading(data, role, o);
  const value = r.available ? short(r.value, 32) : "—";
  const width =
    anchor === "middle"
      ? Math.min(x - 24, 376 - x) * 2
      : anchor === "end"
        ? x - 24
        : 376 - x;
  const fitted = Math.min(
    size,
    width / Math.max(1, String(value).length * 0.57),
  );
  return (
    text(x, y, value, fitted, color, anchor, bold) +
    (r.available && r.unit ? text(x, y + 23, r.unit, 11, muted, anchor) : "")
  );
}

function labelFor(role) {
  return role.replace(/([a-z])([A-Z])/g, "$1 $2").replaceAll("-", " ");
}

function icon(name, cx, cy, radius = 32, color = accent) {
  const scale = radius / 32;
  const shapes = {
    power: "M0 -15V1 M-10 -10A18 18 0 1 0 10 -10",
    check: "M-15 0L-5 10L16 -12",
    reset: "M-14 -10A18 18 0 1 1 -17 8 M-14 -10V-23 M-14 -10H-1",
    arrow: "M-19 0H17 M6 -12L18 0L6 12",
    send: "M-19 -14L22 0L-19 14L-9 0Z M-9 0H22",
    hold: "M-10 13V-6Q-10 -13-4 -12V-22Q-4 -28 2 -25V-8 M2 -14Q9 -18 11 -11V-5 M11 -8Q18 -10 19 -1V9Q19 21 5 22L-4 22L-20 5Q-25 -4-19 -4L-10 3",
    camera:
      "M-24 -12H-11L-7 -20H7L11 -12H24V20H-24Z M10 3A10 10 0 1 1-10 3A10 10 0 1 1 10 3",
    ice: "M0 -19V19 M-16 -9L16 9 M-16 9L16 -9 M-5 -15L0 -10L5 -15 M-5 15L0 10L5 15",
    water: "M0 -23Q-21 0-15 13Q0 31 15 13Q21 0 0 -23Z",
    bell: "M-14 12V-2Q-14 -17 0 -17Q14 -17 14 -2V12L19 17H-19Z M-5 22Q0 29 5 22",
  };
  if (name === "none") return "";
  return `<g transform="translate(${cx} ${cy}) scale(${scale})">${path(shapes[name] ?? shapes.arrow, color, 2.4)}</g>`;
}

function actionFace(id, data, o) {
  const isSimple = simple(o),
    svc = data.service ?? {};
  const glyph =
    id === "notification"
      ? "send"
      : id === "momentary" && o.icon === "power"
        ? "hold"
        : o.icon;
  const label =
    id === "notification"
      ? (svc.deliveryState ?? "Delivery state unavailable")
      : o.label;
  let out = "";
  if (isSimple) {
    out += icon(glyph, 200, 113, 27);
    out += text(200, 174, short(label, 32), 23, "var(--text)", "middle", bold);
    if (id === "momentary")
      out += text(200, 204, "Device-defined hold action", 11, muted, "middle");
  } else {
    const grad = svgId(o, "action-halo");
    out += `<defs><radialGradient id="${E(grad)}"><stop stop-color="var(--accent)" stop-opacity=".2"/><stop offset="1" stop-color="var(--accent)" stop-opacity="0"/></radialGradient></defs>`;
    out += circle(200, 116, 62, `url(#${E(grad)})`, "none");
    out += circle(200, 116, 40, "var(--surface)", "var(--grid)", 1);
    out += path(arc(200, 116, 48, -50, 120), accent, 2, 'opacity=".65"');
    out += icon(glyph, 200, 116, 22);
    out += text(200, 192, short(label, 34), 21, "var(--text)", "middle", bold);
    out += text(
      200,
      220,
      id === "momentary"
        ? "Hold behavior follows device policy"
        : id === "notification"
          ? "Explicit action · approved destination"
          : "Action follows device policy",
      10,
      muted,
      "middle",
    );
  }
  if (id === "notification" && o.destination)
    out += text(200, 245, short(o.destination, 42), 11, accent, "middle");
  return out + fault(data);
}

function toggleFace(id, data, o) {
  const state = labelState(data, o),
    isSimple = simple(o);
  const rail = state.active ? accent : "var(--grid)";
  let out = "";
  if (id === "mode") {
    const svc = data.service ?? {};
    const actualMode =
      svc.mode ?? (typeof state.raw === "string" ? state.raw : null);
    const modes = (svc.modes ?? []).filter(
      (name) => !o.visibleModes?.length || o.visibleModes.includes(name),
    );
    out += text(
      200,
      126,
      short(actualMode ?? "Mode unavailable", 24),
      isSimple ? 34 : 40,
      "var(--text)",
      "middle",
      bold,
    );
    if (modes.length && o.display === "segmented") {
      const selected = modes.slice(0, 4),
        width = 344 / selected.length;
      selected.forEach((name, k) => {
        out += pill(
          28 + width * k,
          157,
          width - 6,
          isSimple ? 30 : 38,
          short(name, 14),
          name === actualMode,
          true,
        );
      });
    } else out += text(200, 177, "Reported boat mode", 11, muted, "middle");
    if (o.showReason)
      out += text(
        200,
        229,
        short(svc.reason ?? "Reason unavailable", 46),
        11,
        muted,
        "middle",
      );
    return out + fault(data);
  }
  if (id === "rocker" || id === "vertical-switch") {
    const positions = o.positions?.length
      ? o.positions.slice(0, 6)
      : ["Off", "On"];
    const labels = o.labels?.length ? o.labels : positions;
    const vertical = id === "vertical-switch" || o.orientation === "vertical";
    let index = positions.findIndex(
      (p) => String(p).toLowerCase() === String(state.raw).toLowerCase(),
    );
    if (typeof state.raw === "boolean" && positions.length === 2)
      index = state.raw ? 1 : 0;
    if (
      numeric(state.raw) &&
      Number.isInteger(state.raw) &&
      state.raw >= 0 &&
      state.raw < positions.length
    )
      index = state.raw;
    if (!state.known) index = -1;
    const x = vertical ? 172 : 40,
      y = vertical ? 65 : 103;
    const width = vertical ? 56 : 320,
      height = vertical ? 156 : 62;
    out += rect(
      x,
      y,
      width,
      height,
      "var(--surface)",
      isSimple ? 16 : 25,
      'stroke="var(--grid)"',
    );
    const segment = (vertical ? height : width) / positions.length;
    positions.forEach((p, k) => {
      if (k === index)
        out += rect(
          vertical ? x + 5 : x + segment * k + 5,
          vertical ? y + segment * k + 4 : y + 5,
          vertical ? width - 10 : segment - 10,
          vertical ? segment - 8 : height - 10,
          accent,
          isSimple ? 11 : 20,
          motion,
        );
      const tx = vertical
        ? o.labelPosition === "beside"
          ? 250
          : 200
        : x + segment * (k + 0.5);
      const ty = vertical ? y + segment * (k + 0.5) + 5 : y + height / 2 + 5;
      out += text(
        tx,
        ty,
        short(labels[k] ?? p, vertical ? 14 : 16),
        12,
        k === index && !(vertical && o.labelPosition === "beside")
          ? "var(--background)"
          : k === index
            ? accent
            : muted,
        vertical && o.labelPosition === "beside" ? "start" : "middle",
        bold,
      );
    });
    out += text(
      200,
      244,
      short(state.label, 30),
      16,
      state.known ? "var(--text)" : muted,
      "middle",
    );
  } else {
    out += rect(
      117,
      82,
      166,
      76,
      "var(--surface)",
      38,
      `stroke="${state.active ? accent : "var(--grid)"}" ${motion}`,
    );
    const cx = state.active ? 246 : 154;
    if (!isSimple) {
      out += line(154, 120, 246, 120, rail, 2, 'opacity=".55"');
      out += circle(cx, 120, 34, rail, "none");
    }
    out += circle(
      cx,
      120,
      isSimple ? 27 : 29,
      state.active ? accent : "var(--muted)",
      "none",
    );
    out += text(
      200,
      203,
      short(state.label, 26),
      isSimple ? 27 : 32,
      state.known ? "var(--text)" : muted,
      "middle",
      bold,
    );
    if (!isSimple)
      out += text(200, 227, "Reported device state", 10, muted, "middle");
  }
  return out + fault(data);
}

function dimmerFace(data, o) {
  const r = serviceReading(data, "intensity", o),
    isSimple = simple(o);
  const amount = r.numeric === null ? null : clamp(r.numeric, 0, 100);
  let out = "";
  if (isSimple) {
    if (o.showPercent)
      out += text(
        200,
        146,
        amount === null ? "—" : `${r.value}${r.unit || "%"}`,
        48,
        "var(--text)",
        "middle",
        bold,
      );
    out += rect(48, 193, 304, 8, "var(--grid)", 4);
    if (amount !== null) {
      out += rect(48, 193, (304 * amount) / 100, 8, accent, 4, motion);
      out += circle(48 + (304 * amount) / 100, 197, 8, "var(--text)", "none");
    }
  } else {
    out += path(arc(200, 147, 72, -135, 135), "var(--grid)", 12);
    for (let k = 0; k < 11; k++) {
      const p = point(200, 147, 88, -135 + k * 27);
      out += circle(p.x, p.y, k === 0 || k === 10 ? 2 : 1.2, muted, "none");
    }
    if (amount !== null) {
      out += path(
        arc(200, 147, 72, -135, -135 + amount * 2.7),
        accent,
        12,
        motion,
      );
      const p = point(200, 147, 72, -135 + amount * 2.7);
      out += circle(p.x, p.y, 6, "var(--text)", "none");
    }
    if (o.showPercent)
      out += text(
        200,
        160,
        amount === null ? "—" : `${r.value}${r.unit || "%"}`,
        42,
        "var(--text)",
        "middle",
        bold,
      );
    out += text(
      200,
      185,
      "BRIGHTNESS",
      10,
      muted,
      "middle",
      'letter-spacing="1.5"',
    );
  }
  if (o.display !== "slider")
    out += text(
      200,
      240,
      o.display === "three-button"
        ? "Device-declared off / down / up"
        : "Device-declared down / up",
      10,
      muted,
      "middle",
    );
  return out + fault(data);
}

function lightingFace(data, o) {
  const svc = data.service ?? {},
    isSimple = simple(o);
  const color =
    typeof svc.color === "string" && /^#[a-f0-9]{6}$/i.test(svc.color)
      ? svc.color
      : null;
  const r = serviceReading(data, "intensity", o);
  let out = "";
  if (isSimple) {
    out += circle(
      200,
      116,
      37,
      color ?? "var(--surface)",
      color ?? "var(--grid)",
      1.5,
    );
    out += text(
      200,
      185,
      color ?? "Color unavailable",
      color ? 22 : 17,
      "var(--text)",
      "middle",
      bold,
    );
  } else {
    const gradient = svgId(o, "reported-light");
    out += `<defs><radialGradient id="${E(gradient)}"><stop stop-color="${color ?? "var(--grid)"}" stop-opacity=".48"/><stop offset="1" stop-color="${color ?? "var(--grid)"}" stop-opacity="0"/></radialGradient></defs>`;
    if (o.display === "beam" && color) {
      const shapes = {
        up: "M176 137L126 59Q200 26 274 59L224 137Z",
        down: "M176 108L126 207Q200 242 274 207L224 108Z",
        left: "M216 109L108 72Q72 137 108 202L216 161Z",
        right: "M184 109L292 72Q328 137 292 202L184 161Z",
      };
      out +=
        o.beamShape === "circle"
          ? circle(200, 129, 85, `url(#${E(gradient)})`, "none")
          : path(
              shapes[o.beamShape] ?? shapes.down,
              "none",
              0,
              `fill="url(#${E(gradient)})"`,
            );
    }
    out += circle(200, 126, 48, "var(--surface)", "var(--grid)", 1);
    out += circle(200, 126, 32, color ?? "var(--grid)", "none");
    if (o.display === "palette")
      out += text(
        200,
        197,
        color ?? "Color unavailable",
        18,
        "var(--text)",
        "middle",
        bold,
      );
  }
  if (o.showIntensity)
    out += text(
      200,
      237,
      `${r.available ? r.value + (r.unit || "%") : "Intensity unavailable"} · ${o.colorMode}`,
      11,
      muted,
      "middle",
    );
  return out + fault(data);
}

function climateFace(data, o) {
  const svc = data.service ?? {},
    isSimple = simple(o);
  const temperature = serviceReading(data, "temperature", o),
    setpoint = serviceReading(data, "setpoint", o);
  const full = o.layout !== "compact";
  let out = "";
  if (isSimple || !full) {
    out += text(
      38,
      86,
      "TEMPERATURE",
      10,
      muted,
      "start",
      'letter-spacing="1.2"',
    );
    out += metric(data, "temperature", o, 38, 150, isSimple ? 53 : 60);
    out += line(235, 90, 235, 184);
    out += text(268, 112, "SETPOINT", 10, muted);
    out += metric(data, "setpoint", o, 268, 155, 30, "start", accent);
  } else {
    const bounds = o.bounds ?? [16, 30];
    const span = bounds[1] - bounds[0];
    out += path(arc(151, 144, 78, -128, 128), "var(--grid)", 7);
    if (temperature.numeric !== null && span !== 0)
      out += path(
        arc(
          151,
          144,
          78,
          -128,
          -128 + 256 * clamp((temperature.numeric - bounds[0]) / span, 0, 1),
        ),
        accent,
        7,
        motion,
      );
    if (setpoint.numeric !== null && span !== 0) {
      const p = point(
        151,
        144,
        78,
        -128 + 256 * clamp((setpoint.numeric - bounds[0]) / span, 0, 1),
      );
      out += circle(p.x, p.y, 6, "var(--text)", "var(--surface)", 2);
    }
    out += text(
      151,
      148,
      temperature.available ? temperature.value : "—",
      44,
      "var(--text)",
      "middle",
      bold,
    );
    out += text(
      151,
      173,
      temperature.unit || "Temperature",
      11,
      muted,
      "middle",
    );
    out += text(
      274,
      99,
      "SETPOINT",
      10,
      muted,
      "start",
      'letter-spacing="1.2"',
    );
    out += metric(data, "setpoint", o, 274, 140, 28, "start", accent);
    out += text(274, 192, "Reported target", 9, muted);
  }
  if (o.showMode)
    out += pill(
      31,
      220,
      150,
      27,
      short(svc.mode ?? "Mode unavailable", 22),
      false,
      true,
    );
  if (o.showFan)
    out += text(367, 238, `Fan ${svc.fan ?? "—"}`, 11, muted, "end");
  return out + fault(data);
}

function machineFace(id, data, o) {
  const svc = data.service ?? {},
    isSimple = simple(o);
  const fields = (o.fields ?? []).slice(
    0,
    isSimple || o.view === "compact" ? 2 : 3,
  );
  if (svc.available === false) return missing("Device service unavailable");
  let out = "";
  if (isSimple) {
    out += icon(id === "ice-maker" ? "ice" : "water", 61, 101, 19);
    const first = fields[0] ?? (id === "ice-maker" ? "state" : "production");
    out += text(106, 91, labelFor(first).toUpperCase(), 10, muted);
    out += metric(data, first, o, 106, 137, 39);
    const second = fields[1];
    if (second) {
      out += line(32, 181, 368, 181);
      const r = serviceReading(data, second, o);
      out += text(32, 209, labelFor(second), 12, muted);
      out += text(
        368,
        210,
        r.available ? `${r.value} ${r.unit}` : "—",
        20,
        "var(--text)",
        "end",
        bold,
      );
    }
  } else {
    out += circle(76, 99, 37, "var(--surface)", "var(--grid)", 1);
    out += icon(id === "ice-maker" ? "ice" : "water", 76, 99, 22);
    if (o.deviceLabel)
      out += text(128, 81, short(svc.name ?? "", 30), 14, muted);
    const stateSample = sample(data, "state");
    out += text(
      128,
      111,
      short(
        stateSample?.value ??
          svc.stateLabel ??
          (svc.state == null ? "Unknown state" : String(svc.state)),
        24,
      ),
      24,
      "var(--text)",
      "start",
      bold,
    );
    const metrics = fields.filter((role) => role !== "state");
    metrics.slice(0, 2).forEach((role, k) => {
      const x = 32 + k * 179;
      out += rect(x, 148, 157, 73, "var(--surface)", 16);
      out += text(x + 15, 169, labelFor(role).toUpperCase(), 9, muted);
      out += metric(data, role, o, x + 15, 199, 25);
    });
  }
  if (id === "ice-maker" && o.showFaults) out += fault(data);
  if (id === "watermaker" && o.showQuality)
    out += text(
      200,
      247,
      short(svc.waterQuality ?? "Quality unavailable", 48),
      10,
      muted,
      "middle",
    );
  return out || missing("Device data unavailable");
}

function stereoFace(data, o) {
  const svc = data.service ?? {},
    isSimple = simple(o);
  let out = "";
  if (o.nowPlaying) {
    out += text(
      32,
      92,
      short(svc.track ?? "Track unavailable", 30),
      isSimple ? 23 : 25,
      "var(--text)",
      "start",
      bold,
    );
    out += text(32, 115, short(svc.artist ?? "", 40), 11, muted);
  }
  const zones = (svc.zones ?? [])
    .filter((z) => !o.zones?.length || o.zones.includes(z.id))
    .slice(0, 4);
  zones.forEach((zone, k) => {
    const y = (o.nowPlaying ? 151 : 91) + k * 29;
    const volume = numeric(zone.volume) ? clamp(zone.volume, 0, 100) : null;
    out += text(32, y, short(zone.name ?? zone.id, 13), 11, muted);
    out += rect(125, y - 8, 193, isSimple ? 5 : 9, "var(--grid)", 5);
    if (volume !== null) {
      out += rect(
        125,
        y - 8,
        (193 * volume) / 100,
        isSimple ? 5 : 9,
        accent,
        5,
        motion,
      );
      if (!isSimple)
        out += circle(
          125 + (193 * volume) / 100,
          y - 3.5,
          5,
          "var(--text)",
          "none",
        );
    }
    out += text(
      368,
      y + 1,
      volume === null ? "—" : Math.round(volume),
      14,
      "var(--text)",
      "end",
      bold,
    );
  });
  if (o.sourceSelector && svc.sourceName)
    out += text(32, 251, short(svc.sourceName, 30), 10, muted);
  return out || missing("Audio service unavailable");
}

function alertsFace(data, o) {
  const svc = data.service ?? {},
    isSimple = simple(o);
  const alerts = [...(svc.alerts ?? [])].filter(
    (a) =>
      (!o.severityFilters?.length || o.severityFilters.includes(a.severity)) &&
      (!o.categoryFilters?.length || o.categoryFilters.includes(a.category)),
  );
  alerts.sort(
    o.order === "time"
      ? (a, b) =>
          (numeric(b.time) ? b.time : 0) - (numeric(a.time) ? a.time : 0)
      : (a, b) =>
          (({ critical: 0, warning: 1, info: 2 })[a.severity] ?? 3) -
          ({ critical: 0, warning: 1, info: 2 }[b.severity] ?? 3),
  );
  if (!alerts.length)
    return (
      icon("bell", 200, 115, 29, muted) +
      text(
        200,
        181,
        svc.available
          ? "No matching active alerts"
          : "Alarm service unavailable",
        16,
        muted,
        "middle",
      )
    );
  const count = Math.min(isSimple ? 4 : 4, o.maxRows ?? 5);
  let out = "";
  alerts.slice(0, count).forEach((a, k) => {
    const y = 65 + k * 43;
    const color =
      a.severity === "critical"
        ? "var(--danger)"
        : a.severity === "warning"
          ? "var(--attention)"
          : accent;
    if (!isSimple) out += rect(28, y, 344, 36, "var(--surface)", 11);
    out += rect(isSimple ? 30 : 28, y + 8, isSimple ? 3 : 4, 20, color, 2);
    out += text(
      43,
      y + 23,
      short(a.label, isSimple ? 38 : 33),
      12,
      "var(--text)",
      "start",
      bold,
    );
    if (!isSimple)
      out += text(
        359,
        y + 23,
        a.severity === "critical" ? "!" : a.severity === "warning" ? "△" : "·",
        14,
        color,
        "end",
      );
  });
  if (alerts.length > count)
    out += text(
      366,
      252,
      `${alerts.length - count} more matching alerts`,
      10,
      muted,
      "end",
    );
  return out;
}

function watchFace(data, o) {
  const svc = data.service ?? {},
    isSimple = simple(o);
  const value =
    o.display === "countdown"
      ? durationText(svc.remaining, "hh:mm:ss")
      : (svc.state ?? "Unavailable");
  const color = svc.phase === "warning" ? "var(--attention)" : "var(--text)";
  let out = "";
  if (!isSimple) {
    out += rect(41, 80, 318, 114, "var(--surface)", 27, 'stroke="var(--grid)"');
    out += circle(
      67,
      103,
      3,
      svc.phase === "warning" ? "var(--attention)" : muted,
      "none",
    );
    out += text(80, 107, "WATCH", 9, muted, "start", 'letter-spacing="1.5"');
    out += text(200, 160, short(value, 22), 42, color, "middle", bold);
  } else out += text(200, 148, short(value, 22), 48, color, "middle", bold);
  out += text(
    200,
    isSimple ? 181 : 225,
    short(svc.phase ?? "Authoritative watch service required", 42),
    12,
    muted,
    "middle",
  );
  return out;
}

function safeURL(url) {
  if (typeof url !== "string") return false;
  try {
    const parsed = new URL(url, "https://neomaris.invalid");
    return ["http:", "https:", "file:", "blob:"].includes(parsed.protocol);
  } catch {
    return false;
  }
}

function mediaFace(id, data, o, context) {
  const isSimple = simple(o),
    media = data.media ?? {};
  if (id === "note") {
    const width = o.style === "heading" ? 28 : 42;
    const words = String(o.content ?? "").split(/\s+/),
      lines = [];
    let current = "";
    for (const word of words) {
      if (current.length + word.length > width && current) {
        lines.push(current);
        current = "";
      }
      current += (current ? " " : "") + word;
    }
    if (current) lines.push(current);
    const centered = o.alignment === "center";
    const size = o.style === "heading" ? 25 : o.style === "label" ? 19 : 16;
    const visible = o.wrap
      ? lines.slice(0, o.style === "heading" ? 5 : 6)
      : [short(o.content, width)];
    let out = !isSimple
      ? rect(28, 60, 344, 183, "var(--surface)", 18) +
        rect(28, 78, 3, 130, accent, 1.5)
      : "";
    out += visible
      .map((s, k) =>
        text(
          centered ? 200 : isSimple ? 32 : 48,
          92 + k * 25,
          s,
          size,
          "var(--text)",
          centered ? "middle" : "start",
          o.style === "heading" ? bold : "",
        ),
      )
      .join("");
    if (o.procedureLink)
      out += text(
        367,
        253,
        `↗ ${short(o.procedureLink, 36)}`,
        10,
        accent,
        "end",
      );
    return out;
  }
  if (id === "divider") {
    if (
      (o.visibility === "compact-only" && o.density !== "compact") ||
      (o.visibility === "comfortable-only" && o.density !== "comfortable")
    )
      return "";
    if (o.style === "space")
      return o.label
        ? text(
            200,
            153,
            short(o.label, 35),
            15,
            muted,
            "middle",
            part("caption"),
          )
        : "";
    let out = "";
    const y = o.style === "heading" ? 174 : 151;
    if (isSimple)
      out += line(
        32,
        y,
        368,
        y,
        "var(--grid)",
        o.spacing === "large" ? 2 : 1,
        part("rule"),
      );
    else {
      out += line(32, y, 368, y, "var(--grid)", 1, part("rule"));
      out += line(
        32,
        y,
        84,
        y,
        accent,
        o.spacing === "large" ? 4 : 2,
        part("rule"),
      );
      out += circle(368, y, 3, accent, "none", 1, part("rule"));
    }
    if (o.label)
      out += text(
        o.style === "heading" ? 32 : 200,
        o.style === "heading" ? 142 : 185,
        short(o.label, 36),
        o.style === "heading" ? 27 : 14,
        o.style === "heading" ? "var(--text)" : muted,
        o.style === "heading" ? "start" : "middle",
        part("caption", bold),
      );
    return out;
  }
  if (id === "diagram") {
    let out = "";
    const approved = media.approved && safeURL(media.url);
    if (!approved) {
      if (!isSimple) out += rect(28, 60, 344, 183, "var(--surface)", 18);
      out += path(
        "M72 153Q116 91 200 72Q284 91 328 153L297 210H103Z",
        "var(--grid)",
        isSimple ? 1.5 : 2.2,
      );
      out += path(
        "M91 153Q133 108 200 91Q267 108 309 153L286 195H114Z",
        "var(--grid)",
        1,
        'opacity=".65"',
      );
      out += line(200, 93, 200, 196, "var(--grid)", 1);
      out += rect(
        154,
        137,
        92,
        44,
        "var(--background)",
        11,
        'stroke="var(--grid)"',
      );
    }
    for (const overlay of o.overlays ?? []) {
      if (typeof context.renderControl !== "function") continue;
      const controlId = overlay.controlId ?? "number";
      const definition = getDefinition(controlId),
        role = Object.keys(definition.roles)[0];
      const width = 352 * (overlay.width ?? 0.2),
        height = Math.min(168, (width * 300) / 400);
      const x = clamp(24 + overlay.x * 352, 24 + width / 2, 376 - width / 2);
      const y = clamp(62 + overlay.y * 168, 62 + height / 2, 230 - height / 2);
      const datum = sample(data, overlay.role);
      const child = createInstance(controlId, {
        id: `overlay-${overlay.role}`,
        options: {
          title: overlay.label || overlay.role,
          showTitle: false,
          showSource: false,
          showAge: false,
          surface: "transparent",
          variant: o.variant,
          theme: o.theme,
          accent: o.accent,
          font: o.font,
          unit: o.roleUnits?.[overlay.role] ?? o.unit,
          decimals: o.roleDecimals?.[overlay.role] ?? o.decimals,
        },
      });
      const rendered = context.renderControl(child, {
        values: { [role]: datum },
        quality: datum?.quality ?? "missing",
      });
      out += rendered.svg.replace(
        "<svg ",
        `<svg x="${x - width / 2}" y="${y - height / 2}" width="${width}" height="${height}" data-overlay-role="${E(overlay.role)}" `,
      );
      if (o.labels)
        out += text(
          x,
          Math.min(250, y + height / 2 + 11),
          short(overlay.label || overlay.role, 18),
          9,
          muted,
          "middle",
        );
    }
    return out;
  }
  const approved = Boolean(media.approved && safeURL(media.url));
  let out = "";
  if (o.border !== "none")
    out += rect(
      24,
      62,
      352,
      168,
      "var(--surface)",
      isSimple ? 12 : 19,
      'stroke="var(--grid)"',
    );
  if (!approved) {
    out += icon(id === "web-page" ? "arrow" : "camera", 200, 124, 25, muted);
    out += text(
      200,
      182,
      id === "web-page"
        ? "Web source unavailable"
        : "Camera source unavailable",
      14,
      muted,
      "middle",
    );
    if (!isSimple)
      out += text(
        200,
        204,
        "Host-approved source required",
        10,
        muted,
        "middle",
      );
  }
  const name =
    o.showName === false
      ? ""
      : short(media.name ?? (id === "web-page" ? "Web source" : "Camera"), 24);
  if (name) out += text(28, 247, name, 11, "var(--text)");
  if (o.showTime && media.time != null) {
    const measured = numeric(media.time)
      ? new Date(media.time)
      : new Date(String(media.time));
    const stamp = Number.isFinite(measured.getTime())
      ? `${pad(measured.getUTCHours())}:${pad(measured.getUTCMinutes())}:${pad(measured.getUTCSeconds())} UTC`
      : "Time unavailable";
    out += text(372, 247, stamp, 10, muted, "end");
  } else
    out += text(
      372,
      247,
      approved ? "Approved source" : "Unavailable",
      10,
      muted,
      "end",
    );
  return out;
}

export function renderSystem(id, data, o, context = {}) {
  if (["action", "momentary", "notification"].includes(id))
    return actionFace(id, data, o);
  if (["active-button", "rocker", "vertical-switch", "mode"].includes(id))
    return toggleFace(id, data, o);
  if (id === "dimmer") return dimmerFace(data, o);
  if (id === "lighting-color") return lightingFace(data, o);
  if (id === "climate") return climateFace(data, o);
  if (["ice-maker", "watermaker"].includes(id)) return machineFace(id, data, o);
  if (id === "stereo") return stereoFace(data, o);
  if (id === "alerts") return alertsFace(data, o);
  if (id === "watchkeeping") return watchFace(data, o);
  if (
    ["camera", "camera-ptz", "web-page", "diagram", "note", "divider"].includes(
      id,
    )
  )
    return mediaFace(id, data, o, context);
  throw new Error(`No system renderer for ${id}`);
}
